// Phase 0 (IMPLEMENTATION §0.1, §2.3): applies one command at dispatch. Every branch validates first and
// mutates only on success, so a rejected command leaves the state untouched (replay re-rejects it) — with
// one deliberate exception: any `drop` ends the drag, so it clears `held` whatever its source and even when
// the drop is refused. An unknown command kind or dbg op (untyped JSON via __gm or a trace) rejects `stale`.
// After every command a held entity that no longer exists (consumed, sold, merged away) is released.
// v0.2: `setMode` rejects `notUnlocked`.
import { packerCap, packerDef } from '../bdefs.ts';
import {
  applyBmerge,
  applyBmove,
  applyBsell,
  applyBswap,
  applyBuyMerge,
  applyBuyPlace,
  applyPaint,
  applyUndoBuild,
  erasableCount,
  eraseChutes,
  rotateBuilding,
  undoBuildBlock,
} from '../buildOps.ts';
import { type CmdResult, type Command, type Dst, ok, reject, type Src } from '../commands.ts';
import { grabbable, insertInto, releaseVanishedHeld, storedAt, takeStored } from '../containers.ts';
import { payoutOf } from '../core/num.ts';
import { type Int, isInt } from '../core/types.ts';
import type { Ctx } from '../ctx.ts';
import {
  addItem,
  buildingAt,
  depositAt,
  discover,
  famDef,
  findBuilding,
  findItem,
  isValidItem,
  itemValue,
  moveItem,
  removeItem,
  restoreItem,
} from '../entities.ts';
import { applyExpand, expandBlock } from '../expansions.ts';
import type { FamId } from '../gen/ids.ts';
import { cancelGhost, deliverToGhost, ghostFill } from '../ghosts.ts';
import { book } from '../ledger.ts';
import { isFree, isOpen } from '../occupancy.ts';
import { crateTotal } from '../rules/accept.ts';
import { bsellOutcome, type DropOutcome, resolveDrop } from '../rules/drop.ts';
import {
  cascadeMerge,
  mergeAllBlock,
  mergeAllFamilies,
  mergeItems,
  mergeTaken,
  quickMergeBlock,
} from '../rules/merge.ts';
import { planPaint } from '../rules/paint.ts';
import { planUndoSell } from '../rules/undo.ts';
import { sellCrate, sellItems, sellTaken, undoSale } from '../sales.ts';
import { clearStall, raiseStall } from '../stall.ts';
import type { Building, FloorItem, ItemRef } from '../state.ts';
import { skipTipsBefore } from '../tutorial.ts';
import { applyAllUnlocks, applyUnlocksUpTo, hasEffect } from '../unlocks.ts';
import { tapDeposit } from './depositSystem.ts';
import { gunStats } from './gallerySystem.ts';

/** Takes the dragged item off the board (floor or slot), keeping its id; null if it moved meanwhile. */
function takeDragged(c: Ctx, from: Src): { r: ItemRef; x: Int; y: Int } | null {
  if (from.k === 'cell') {
    const it = findItem(c.s, from.id);
    if (it === null) return null;
    removeItem(c, it.id);
    return { r: { id: it.id, fam: it.fam, tier: it.tier }, x: it.x, y: it.y };
  }
  if (from.k === 'slot') {
    const at = storedAt(c, from.bid, from.slot, from.idx, from.id);
    if (at === null) return null;
    return { r: takeStored(c, at), x: at.b.x, y: at.b.y };
  }
  return null;
}

function applyItemOutcome(c: Ctx, from: Src, out: DropOutcome): void {
  switch (out.k) {
    case 'move': {
      if (from.k === 'cell') {
        const it = findItem(c.s, out.id) as FloorItem;
        const fx = it.x;
        const fy = it.y;
        moveItem(c, it, out.x, out.y);
        c.ev.push({ e: 'move', id: it.id, x: out.x, y: out.y, fromX: fx, fromY: fy, tag: null });
        return;
      }
      const t = takeDragged(c, from);
      if (t === null) return;
      restoreItem(c, { id: t.r.id, fam: t.r.fam, tier: t.r.tier, x: out.x, y: out.y });
      c.ev.push({ e: 'move', id: t.r.id, x: out.x, y: out.y, fromX: t.x, fromY: t.y, tag: null });
      return;
    }
    case 'merge': {
      const b = findItem(c.s, out.b) as FloorItem;
      if (from.k === 'cell') {
        mergeItems(c, findItem(c.s, out.a) as FloorItem, b, out.x, out.y, 'hand');
      } else {
        const t = takeDragged(c, from);
        if (t === null) return;
        mergeTaken(c, t.r, b, out.x, out.y, 'hand');
      }
      c.s.progress.handMerges += 1;
      return;
    }
    case 'swap': {
      const a = findItem(c.s, out.a) as FloorItem;
      const b = findItem(c.s, out.b) as FloorItem;
      const ax = a.x;
      const ay = a.y;
      const bx = b.x;
      const by = b.y;
      const tag = out.max ? 'max' : null;
      moveItem(c, a, bx, by);
      moveItem(c, b, ax, ay);
      c.ev.push({ e: 'move', id: a.id, x: bx, y: by, fromX: ax, fromY: ay, tag });
      c.ev.push({ e: 'move', id: b.id, x: ax, y: ay, fromX: bx, fromY: by, tag });
      return;
    }
    case 'sell': {
      if (from.k === 'cell') {
        sellItems(c, [out.id], out.value, out.where);
        return;
      }
      const t = takeDragged(c, from);
      if (t === null) return;
      sellTaken(c, [{ ...t.r, x: t.x, y: t.y }], out.value, out.where);
      return;
    }
    case 'insert': {
      const b = findBuilding(c.s, out.bid) as Building;
      const t = takeDragged(c, from);
      if (t === null) return;
      insertInto(c, b, t.r, 1);
      return;
    }
    case 'deliver': {
      const b = findBuilding(c.s, out.bid) as Building;
      const t = takeDragged(c, from);
      if (t === null) return;
      const fill = ghostFill(c.d, b, t.r.fam, t.r.tier);
      if (fill === null) throw new Error('deliver: the ghost no longer needs the item');
      deliverToGhost(c, b, t.r, fill);
      return;
    }
    default:
      return;
  }
}

function cmdDrop(c: Ctx, from: Src, to: Dst, build: boolean): CmdResult {
  const out = resolveDrop(c.s, c.d, c.occ, from, to, build);
  c.s.transient.held = null;
  switch (out.k) {
    case 'reject':
      c.ev.push({
        e: 'reject',
        id: typeof from === 'object' && from !== null && 'id' in from ? from.id : 0,
        reason: out.reason,
      });
      return reject(out.reason);
    case 'none':
      return ok();
    case 'buyPlace':
      applyBuyPlace(c, out);
      return ok();
    case 'buyMerge':
      applyBuyMerge(c, out);
      return ok();
    case 'bmove':
      applyBmove(c, out);
      return ok();
    case 'bswap':
      applyBswap(c, out);
      return ok();
    case 'bmerge':
      applyBmerge(c, out);
      return ok();
    case 'bsell':
      applyBsell(c, out);
      return ok();
    default:
      applyItemOutcome(c, from, out);
      return ok();
  }
}

function levelCap(c: Ctx, type: string): Int {
  if (type === 'deposit') return c.d.depositRules.oddsByLevel.length;
  if (type === 'drill') return c.d.buildings.drill.hitTicks.length;
  if (type === 'crate') return c.d.buildings.crate.capacity.length;
  if (type === 'chute') return 1;
  return c.d.machineRules.maxLevel;
}

function cmdDbg(c: Ctx, cmd: Extract<Command, { c: 'dbg' }>): CmdResult {
  switch (cmd.op) {
    case 'cash': {
      if (!isInt(cmd.a)) return reject('stale');
      if (c.s.wallet.cash + cmd.a < 0) return reject('noCash');
      if (!isInt(c.s.wallet.cash + cmd.a)) return reject('max'); // cash stays a safe integer (C# long)
      c.s.wallet.cash += cmd.a;
      return ok();
    }
    case 'spawn': {
      const { fam, tier, x, y } = cmd;
      if (fam === undefined || !isInt(tier) || !isInt(x) || !isInt(y)) return reject('stale');
      if (!isValidItem(c.d, fam as FamId, tier)) return reject('notAccepted');
      if (!isOpen(c.occ, x, y)) return reject('locked');
      if (!isFree(c.occ, x, y)) return reject('occupied');
      const it = addItem(c, fam, tier, x, y);
      book(c.s, c.d, fam, tier, 'granted', 1);
      discover(c, fam, tier);
      c.ev.push({ e: 'spawn', id: it.id, fam, tier, x, y, from: 0 });
      return ok();
    }
    case 'level': {
      const { a, x, y } = cmd;
      if (!isInt(a) || !isInt(x) || !isInt(y)) return reject('stale');
      const b = buildingAt(c, x, y);
      if (b === null) return reject('stale');
      if (a < 1 || a > levelCap(c, b.type)) return reject('max');
      if (b.type === 'gallery' && a > c.d.buildings.gallery.gunCapByLevel.length)
        return reject('max');
      if (b.type === 'crate') {
        // never below the current contents
        if (crateTotal(b) > (c.d.buildings.crate.capacity[a - 1] ?? 0)) return reject('full');
      }
      if (b.type === 'smelter') {
        const cap = c.d.buildings.smelter.heatCap[a - 1] ?? 0;
        if (b.heat > cap) b.heat = cap;
      }
      if (b.type === 'packer') {
        const caps = packerDef(c.d).targetCapByLevel;
        if (b.target === caps[b.level - 1]) b.target = caps[a - 1] ?? b.target;
      }
      b.level = a;
      return ok();
    }
    case 'unlockAll':
      applyAllUnlocks(c);
      return ok();
    case 'goal': {
      // goal jump: every active unlock up to and including table index `a`, with effects, in order, as if
      // played (u01 empties the practice tin first); tips of earlier phases retire silently
      const { a } = cmd;
      if (!isInt(a) || a < 0 || a >= c.d.unlocks.length) return reject('stale');
      applyUnlocksUpTo(c, a, true);
      skipTipsBefore(c, a);
      return ok();
    }
    default:
      return unknownCommand(cmd.op);
  }
}

/** Exhaustiveness guard; at runtime an unknown kind/op (untyped JSON) is refused like a stale command. */
function unknownCommand(_: never): CmdResult {
  return reject('stale');
}

/** R9 practice mode (cold open, u00): each tap fires one practice round from the tin; the tap after the
 * last round clicks empty and ends practice mode. Practice rounds are not items (never in the ledger). */
function cmdTapGallery(c: Ctx, bid: Int): CmdResult {
  const g = findBuilding(c.s, bid);
  if (g === null || g.type !== 'gallery') return reject('stale');
  if (!g.manual) return reject('notUnlocked');
  if (g.practice <= 0) {
    g.manual = false;
    c.ev.push({ e: 'empty', bid: g.id });
    return ok();
  }
  if (g.gun === null) return reject('stale');
  const tier = c.d.economy.coldOpen.ammoTier;
  const payout = payoutOf(itemValue(c.d, 'ammo', tier), gunStats(c, g.gun.tier).multPermille);
  g.practice -= 1;
  c.s.wallet.cash += payout;
  c.s.wallet.lifetime += payout;
  c.s.income.accT += payout;
  c.ev.push({ e: 'fire', bid: g.id, gunTier: g.gun.tier, ammoTier: tier, payout, practice: true });
  return ok();
}

function cmdPause(c: Ctx, bid: Int, on: boolean): CmdResult {
  const b = findBuilding(c.s, bid);
  if (b === null || b.type === 'deposit' || b.type === 'chute' || b.ghost !== null) {
    return reject('stale');
  }
  if (typeof on !== 'boolean') return reject('stale');
  if (b.paused === on) return ok();
  b.paused = on;
  if (on) raiseStall(c, b, 'PAUSED');
  else clearStall(c, b);
  return ok();
}

/** R5 target picker: 2 … the cap (level cap limited by the family maximum); the cap = "default". */
function cmdSetTarget(c: Ctx, bid: Int, tier: Int): CmdResult {
  const b = findBuilding(c.s, bid);
  if (b === null || b.type !== 'packer' || !isInt(tier) || tier < 2) return reject('stale');
  const cap = packerCap(c.d, b.level, b.lockFam);
  if (tier > cap) return reject('max');
  b.target = tier === cap ? (packerDef(c.d).targetCapByLevel[b.level - 1] ?? cap) : tier;
  return ok();
}

function dispatch(c: Ctx, cmd: Command): CmdResult {
  switch (cmd.c) {
    case 'hit': {
      const dep = depositAt(c, cmd.x, cmd.y);
      if (dep === null) return reject('stale');
      return tapDeposit(c, dep) ? ok() : reject('cooldown');
    }
    case 'hold': {
      if (depositAt(c, cmd.x, cmd.y) === null) return reject('stale');
      c.s.transient.hold = { x: cmd.x, y: cmd.y, t: 0 };
      return ok();
    }
    case 'holdEnd':
      c.s.transient.hold = null;
      return ok();
    case 'grab': {
      if (!grabbable(c, cmd.id)) return reject('stale');
      c.s.transient.held = cmd.id;
      return ok();
    }
    case 'release':
      c.s.transient.held = null;
      return ok();
    case 'drop':
      return cmdDrop(c, cmd.from, cmd.to, cmd.build);
    case 'undoSell': {
      const plan = planUndoSell(c.s, c.d, c.occ, cmd.token);
      if (!plan.ok) return reject(plan.reason);
      undoSale(c, plan.cells);
      return ok();
    }
    case 'quickMerge': {
      const why = quickMergeBlock(c.s, c.d, cmd.id);
      if (why !== null) return reject(why);
      const it = findItem(c.s, cmd.id) as FloorItem;
      cascadeMerge(c, [famDef(c.d, it.fam)], it.tier, it.id, 'quick');
      return ok();
    }
    case 'mergeAll': {
      const why = mergeAllBlock(c.s, c.d);
      if (why !== null) return reject(why);
      const n = cascadeMerge(c, mergeAllFamilies(c.d), 1, 0, 'all');
      c.s.progress.mergeAllReadyAt = c.s.tick + c.d.economy.mergeAllCooldownTicks;
      c.ev.push({ e: 'mergeAll', n });
      return ok();
    }
    case 'dbg':
      return cmdDbg(c, cmd);
    case 'tapGallery':
      return cmdTapGallery(c, cmd.bid);
    case 'crateSellAll': {
      const b = findBuilding(c.s, cmd.bid);
      if (b === null || b.type !== 'crate' || b.ghost !== null) return reject('stale');
      if (b.fam === null || crateTotal(b) === 0) return reject('stale'); // nothing to sell
      sellCrate(c, b);
      return ok();
    }
    case 'rotate': {
      const b = findBuilding(c.s, cmd.bid);
      if (b === null || b.type === 'deposit') return reject('stale');
      rotateBuilding(c, b);
      return ok();
    }
    case 'pause':
      return cmdPause(c, cmd.bid, cmd.on);
    case 'setTarget':
      return cmdSetTarget(c, cmd.bid, cmd.tier);
    case 'sellBuilding': {
      if (!hasEffect(c.s, c.d, 'buildMode')) return reject('notUnlocked');
      const b = findBuilding(c.s, cmd.bid);
      if (b === null) return reject('stale');
      const out = bsellOutcome(c.s, c.d, b);
      if (out.k !== 'bsell') return reject(out.k === 'reject' ? out.reason : 'stale');
      applyBsell(c, out);
      return ok();
    }
    case 'cancelGhost': {
      const b = findBuilding(c.s, cmd.bid);
      if (b === null || b.ghost === null) return reject('stale');
      cancelGhost(c, b);
      return ok();
    }
    case 'paintChutes': {
      if (!hasEffect(c.s, c.d, 'buildMode') || !hasEffect(c.s, c.d, 'build.chute')) {
        return reject('notUnlocked');
      }
      const plan = planPaint(c.s, c.d, c.occ, cmd.path);
      if (!plan.ok) return reject(plan.reason);
      applyPaint(c, plan);
      return ok();
    }
    case 'eraseChutes': {
      if (!hasEffect(c.s, c.d, 'buildMode')) return reject('notUnlocked');
      if (!Array.isArray(cmd.cells) || erasableCount(c, cmd.cells) === 0) return reject('stale');
      eraseChutes(c, cmd.cells);
      return ok();
    }
    case 'undoBuild': {
      if (!hasEffect(c.s, c.d, 'buildMode')) return reject('notUnlocked');
      const why = undoBuildBlock(c.s, c);
      if (why !== null) return reject(why);
      applyUndoBuild(c);
      return ok();
    }
    case 'expand': {
      const why = expandBlock(c.s, c.d, cmd.id);
      if (why !== null) return reject(why);
      applyExpand(c, cmd.id);
      return ok();
    }
    // v0.2: Press modes.
    case 'setMode':
      return reject('notUnlocked');
    default:
      return unknownCommand(cmd);
  }
}

export function applyCommand(c: Ctx, cmd: Command): CmdResult {
  if (typeof cmd !== 'object' || cmd === null) return reject('stale');
  const r = dispatch(c, cmd);
  if (r.ok) releaseVanishedHeld(c);
  return r;
}
